/**
 * Enhanced logging utility with more detailed state tracking
 */

// Set to false in production to disable most logs
const DEBUG_MODE = true;

// Set to true to enable component render tracking
const TRACE_RENDERS = true;

// Helper to get component props in a safe way
const safeStringify = (obj: any): string => {
  try {
    // Replace circular references with '[Circular]'
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      // Skip functions and DOM elements
      if (typeof value === 'function' || 
          (value && typeof value === 'object' && value.nodeType)) {
        return '[Function/DOM]';
      }
      return value;
    }, 2);
  } catch (err) {
    return `[Error stringifying: ${err}]`;
  }
};

// Unique ID tracker for component instances
let nextInstanceId = 1;
const getInstanceId = () => nextInstanceId++;

export const logger = {
  /**
   * Track component renders with details about props and state
   */
  trace: (component: string, phase: 'render' | 'mount' | 'update' | 'unmount', props?: any, state?: any) => {
    if (!DEBUG_MODE || !TRACE_RENDERS) return;
    
    const timestamp = new Date().toISOString();
    const propsStr = props ? safeStringify(props) : '';
    const stateStr = state ? safeStringify(state) : '';
    
    const instanceId = getInstanceId();
    
    console.group(`[${timestamp}][${component}:${instanceId}] 🔍 ${phase}`);
    if (props) console.log(`Props:`, props);
    if (state) console.log(`State:`, state);
    console.groupEnd();
  },

  /**
   * Debug level logs - only shown when DEBUG_MODE is true
   * For detailed tracing info for developers
   */
  debug: (context: string, message: string, data?: any) => {
    if (!DEBUG_MODE) return;
    const timestamp = new Date().toISOString();
    if (data) {
      console.debug(`[${timestamp}][${context}] 🔍 ${message}`, data);
    } else {
      console.debug(`[${timestamp}][${context}] 🔍 ${message}`);
    }
  },
  
  /**
   * Info level logs - only shown when DEBUG_MODE is true
   * For general information about application flow
   */
  info: (context: string, message: string, data?: any) => {
    if (!DEBUG_MODE) return;
    const timestamp = new Date().toISOString();
    if (data) {
      console.info(`[${timestamp}][${context}] ℹ️ ${message}`, data);
    } else {
      console.info(`[${timestamp}][${context}] ℹ️ ${message}`);
    }
  },
  
  /**
   * Warning level logs - always shown
   * For potential issues that aren't errors
   */
  warn: (context: string, message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    if (data) {
      console.warn(`[${timestamp}][${context}] ⚠️ ${message}`, data);
    } else {
      console.warn(`[${timestamp}][${context}] ⚠️ ${message}`);
    }
  },
  
  /**
   * Error level logs - always shown
   * For actual errors that need attention
   */
  error: (context: string, message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    if (data) {
      console.error(`[${timestamp}][${context}] 🔴 ${message}`, data);
    } else {
      console.error(`[${timestamp}][${context}] 🔴 ${message}`);
    }
  },
  
  /**
   * Auth specific logging to track state changes
   */
  authState: (message: string, authData: any) => {
    if (!DEBUG_MODE) return;
    const timestamp = new Date().toISOString();
    console.group(`[${timestamp}][AuthState] 🔑 ${message}`);
    if (authData.user) console.log(`User:`, authData.user.email || '[No Email]');
    console.log(`Auth Version:`, authData.authVersion);
    console.log(`Is Anonymous:`, authData.userProfile?.isAnonymous || 'No profile');
    console.log(`Session ID:`, authData.sessionId || 'None');
    console.groupEnd();
  }
};